<script>
  import { Icon, Button } from "./ui/index.js";
  import { fly, fade } from "svelte/transition";
  import { cubicOut } from "svelte/easing";

  /**
   * @typedef {Object} Notice
   * @property {string} id
   * @property {'banner'|'modal'} type
   * @property {'info'|'warning'|'error'} severity
   * @property {string} title
   * @property {string} message
   * @property {string} [linkText]
   * @property {string} [linkUrl]
   * @property {boolean} dismissable
   * @property {string} [buttonText]
   */

  /** @type {{ notices: Notice[] }} */
  let { notices = [] } = $props();

  // Track dismissed notices in localStorage
  const DISMISSED_KEY = "pd_dismissed_notices";

  function getDismissedNotices() {
    try {
      const stored = localStorage.getItem(DISMISSED_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  function saveDismissedNotice(id) {
    const dismissed = getDismissedNotices();
    if (!dismissed.includes(id)) {
      dismissed.push(id);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
    }
  }

  function dismissCurrentNotice() {
    if (!currentModal) return;
    // Save to localStorage
    saveDismissedNotice(currentModal.id);
    // Update local state to trigger re-render
    dismissedIds = [...dismissedIds, currentModal.id];
    // Don't increment index - the array will shrink and show the next one
  }

  function acknowledgeCurrentNotice() {
    // Non-dismissable - add to session-only acknowledged list
    if (!currentModal) return;
    acknowledgedIds = [...acknowledgedIds, currentModal.id];
  }

  // Local state
  let dismissedIds = $state(getDismissedNotices());
  let acknowledgedIds = $state([]); // Session-only for non-dismissable notices

  // Filter to only modal notices that aren't dismissed or acknowledged
  let pendingModals = $derived(
    notices
      .filter((n) => n.type === "modal")
      .filter((n) => !n.dismissable || !dismissedIds.includes(n.id))
      .filter((n) => !acknowledgedIds.includes(n.id))
  );

  // Current modal to show (always first in list since we filter out dismissed/acknowledged)
  let currentModal = $derived(pendingModals[0] || null);

  // Severity styles
  const severityStyles = {
    info: {
      headerBg: "var(--color-primary-600)",
      icon: "info",
    },
    warning: {
      headerBg: "#f59e0b",
      icon: "alertTriangle",
    },
    error: {
      headerBg: "#ef4444",
      icon: "alertCircle",
    },
  };

  // Current modal styles
  let currentStyles = $derived(
    currentModal
      ? severityStyles[currentModal.severity] || severityStyles.info
      : severityStyles.info
  );
</script>

{#if currentModal}
  <div
    class="modal-overlay"
    role="dialog"
    aria-modal="true"
    transition:fade={{ duration: 200 }}
  >
    <div
      class="modal-container"
      transition:fly={{ y: 30, duration: 300, easing: cubicOut }}
    >
      <div class="modal-header" style="background: {currentStyles.headerBg};">
        <div class="modal-header-icon">
          <Icon name={currentStyles.icon} size={24} />
        </div>
        <h2 class="modal-title">{currentModal.title || "Notice"}</h2>
      </div>

      <div class="modal-body">
        <p class="modal-message">{currentModal.message}</p>

        {#if currentModal.linkText && currentModal.linkUrl}
          <a
            href={currentModal.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="modal-link"
          >
            {currentModal.linkText}
            <Icon name="externalLink" size={14} />
          </a>
        {/if}
      </div>

      <div class="modal-footer">
        {#if pendingModals.length > 1}
          <span class="modal-counter">
            1 of {pendingModals.length}
          </span>
        {/if}
        {#if currentModal.dismissable}
          <Button onclick={dismissCurrentNotice}>
            {currentModal.buttonText || "Don't show again"}
          </Button>
        {:else}
          <Button variant="primary" onclick={acknowledgeCurrentNotice}>
            {currentModal.buttonText || "I understand"}
          </Button>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    z-index: 1000;
    backdrop-filter: blur(2px);
  }

  .modal-container {
    background: white;
    border-radius: var(--radius-xl);
    width: 100%;
    max-width: 440px;
    max-height: calc(100vh - var(--space-8));
    max-height: calc(100dvh - var(--space-8));
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-4) var(--space-5);
    color: white;
    flex-shrink: 0;
  }

  .modal-header-icon {
    flex-shrink: 0;
  }

  .modal-title {
    font-size: var(--text-lg);
    font-weight: var(--font-semibold);
    margin: 0;
  }

  .modal-body {
    padding: var(--space-5);
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  .modal-message {
    font-size: var(--text-base);
    line-height: 1.6;
    color: var(--color-gray-700);
    margin: 0;
    white-space: pre-wrap;
  }

  .modal-link {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    margin-top: var(--space-4);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-primary-600);
    text-decoration: none;
  }

  .modal-link:hover {
    text-decoration: underline;
  }

  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-3);
    padding: var(--space-4) var(--space-5);
    background: var(--color-gray-50);
    border-top: 1px solid var(--color-gray-200);
    flex-shrink: 0;
  }

  .modal-counter {
    margin-right: auto;
    font-size: var(--text-sm);
    color: var(--color-gray-500);
  }
</style>
